import React, { useLayoutEffect, useRef, useState } from "react";
import { CheckCircle2 } from "lucide-react";
import { formatPhone, formatMoney, moneyDigits, phoneDigits, capitalizeName, US_STATES, yearOptions } from "@/lib/format";

export const inputCls =
  "w-full bg-[#0F0F0F] border border-white/10 px-4 py-3.5 text-base text-white placeholder-neutral-500 focus:border-[#C19B6C] focus:outline-none focus:ring-1 focus:ring-[#C19B6C] transition-colors";
export const labelCls = "block text-xs font-semibold tracking-wider text-neutral-400 uppercase mb-2";

export function FieldWrap({ label, hint, error, children, className = "", optional = false }) {
  return (
    <div className={className}>
      {label && (
        <label className={labelCls}>
          {label}
          {optional && <span className="text-neutral-600 normal-case tracking-normal font-normal"> (optional)</span>}
        </label>
      )}
      {children}
      {hint && !error && <p className="text-xs text-neutral-500 mt-1.5">{hint}</p>}
      {error && <p className="text-xs text-red-400 mt-1.5" role="alert">{error}</p>}
    </div>
  );
}

// Keeps the caret in place while the displayed text is re-formatted around it.
function useCaretPreservingFormat(display) {
  const ref = useRef(null);
  const pending = useRef(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || pending.current === null) return;
    const want = pending.current;
    pending.current = null;
    let count = 0;
    let pos = display.length;
    for (let i = 0; i < display.length; i += 1) {
      if (/[\d.]/.test(display[i])) count += 1;
      if (count >= want) { pos = i + 1; break; }
    }
    if (want === 0) pos = display.startsWith("$") || display.startsWith("(") ? 1 : 0;
    try { el.setSelectionRange(pos, pos); } catch { /* not focusable */ }
  }, [display]);
  const onBeforeFormat = (e) => {
    const raw = e.target.value;
    const caret = e.target.selectionStart ?? raw.length;
    pending.current = (raw.slice(0, caret).match(/[\d.]/g) || []).length;
  };
  return [ref, onBeforeFormat];
}

export function PhoneInput({ value, onChange, testId, ...rest }) {
  const display = formatPhone(value);
  const [ref, before] = useCaretPreservingFormat(display);
  return (
    <input
      ref={ref}
      type="tel"
      inputMode="tel"
      autoComplete="tel"
      placeholder="(916) 555-0123"
      className={inputCls}
      value={display}
      onChange={(e) => { before(e); onChange(phoneDigits(e.target.value)); }}
      data-testid={testId}
      {...rest}
    />
  );
}

// value is the raw numeric string ("618000" or "1950.50"); parent converts with moneyValue() when submitting.
export function MoneyInput({ value, onChange, testId, placeholder = "$0", ...rest }) {
  const display = formatMoney(value);
  const [ref, before] = useCaretPreservingFormat(display);
  return (
    <input
      ref={ref}
      type="text"
      inputMode="decimal"
      placeholder={placeholder}
      className={inputCls}
      value={display}
      onChange={(e) => { before(e); onChange(moneyDigits(e.target.value)); }}
      data-testid={testId}
      {...rest}
    />
  );
}

export function EmailInput({ value, onChange, testId, ...rest }) {
  return (
    <input
      type="email"
      inputMode="email"
      autoComplete="email"
      autoCapitalize="off"
      autoCorrect="off"
      spellCheck={false}
      placeholder="name@example.com"
      className={inputCls}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testId}
      {...rest}
    />
  );
}

export function NameInput({ value, onChange, testId, autoComplete = "given-name", ...rest }) {
  const capitalizedOnce = useRef(false);
  return (
    <input
      type="text"
      autoComplete={autoComplete}
      className={inputCls}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      onBlur={() => {
        if (!capitalizedOnce.current && value.trim()) {
          capitalizedOnce.current = true;
          const fixed = capitalizeName(value);
          if (fixed !== value) onChange(fixed);
        }
      }}
      data-testid={testId}
      {...rest}
    />
  );
}

export function NameFields({ first, last, onChange, errors = {}, testIdPrefix }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <FieldWrap label="First name" error={errors.first_name}>
        <NameInput value={first} onChange={(v) => onChange("first_name", v)} testId={`${testIdPrefix}-first-name`} autoComplete="given-name" />
      </FieldWrap>
      <FieldWrap label="Last name" error={errors.last_name}>
        <NameInput value={last} onChange={(v) => onChange("last_name", v)} testId={`${testIdPrefix}-last-name`} autoComplete="family-name" />
      </FieldWrap>
    </div>
  );
}

export function Select({ value, onChange, options, placeholder = "Select…", testId, ...rest }) {
  return (
    <select className={inputCls} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testId} {...rest}>
      <option value="">{placeholder}</option>
      {options.map((o) => {
        const [v, l] = Array.isArray(o) ? o : [o, o];
        return <option key={v} value={v}>{l}</option>;
      })}
    </select>
  );
}

export function StateSelect(props) {
  return <Select options={US_STATES} placeholder="State" autoComplete="address-level1" {...props} />;
}

const YEARS = yearOptions();
export function YearSelect(props) {
  return <Select options={[...YEARS, ["unknown", "Year unknown"]]} placeholder="Year" {...props} />;
}

export function OptionGrid({ options, value, onChange, testIdPrefix, columns = 2, multiple = false }) {
  const selectedSet = multiple ? new Set(value || []) : null;
  return (
    <div className={`grid grid-cols-1 ${columns === 3 ? "sm:grid-cols-3" : columns === 4 ? "sm:grid-cols-2 md:grid-cols-4" : "sm:grid-cols-2"} gap-3`}>
      {options.map((opt) => {
        const [v, l] = Array.isArray(opt) ? opt : [opt, opt];
        const selected = multiple ? selectedSet.has(v) : value === v;
        return (
          <button
            key={v}
            type="button"
            onClick={() => {
              if (!multiple) return onChange(v);
              const next = new Set(selectedSet);
              if (next.has(v)) next.delete(v); else next.add(v);
              return onChange(options.map((o) => (Array.isArray(o) ? o[0] : o)).filter((x) => next.has(x)));
            }}
            aria-pressed={selected}
            data-testid={`${testIdPrefix}-${String(v).toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            className={`text-left px-5 py-4 border transition-colors ${selected ? "border-[#C19B6C] bg-[#C19B6C]/10 text-white" : "border-white/10 bg-[#0F0F0F] text-neutral-200 hover:border-[#C19B6C]/50"}`}
          >
            <span className="flex items-center justify-between gap-3">
              <span className="text-base">{l}</span>
              {selected && <CheckCircle2 size={16} className="text-[#C19B6C] shrink-0" />}
            </span>
          </button>
        );
      })}
    </div>
  );
}

export function Checkbox({ checked, onChange, label, testId }) {
  const [id] = useState(() => `cb_${Math.random().toString(36).slice(2, 8)}`);
  return (
    <label htmlFor={id} className="flex items-start gap-3 cursor-pointer">
      <input id={id} type="checkbox" className="mt-1 accent-[#C19B6C]" checked={!!checked} onChange={(e) => onChange(e.target.checked)} data-testid={testId} />
      <span className="text-xs text-neutral-400 leading-relaxed">{label}</span>
    </label>
  );
}
